import { word } from "@/modules/core/utils/i18n"
import { UserEntity } from "@/modules/profile/userProfile/userEntity"
import type {
  CommentDto,
  CommentMentionDto,
  GroupedReactionDto,
  ReactionDto,
  ReactionSocialPostDto,
  SocialPostDto,
  SocialPostResumeLightDto,
} from "@/modules/social/gateway/socialDto"
import dayjs from "dayjs"

/* ── Reaction Summary (embedded in post/comment) ── */

export class ReactionSocialPostEntity {
  myReaction: { emoji: string } | null
  allGroupedReactions: GroupedReactionDto[]

  private constructor(data: { myReaction: { emoji: string } | null; allGroupedReactions: GroupedReactionDto[] }) {
    this.myReaction = data.myReaction
    this.allGroupedReactions = data.allGroupedReactions
  }

  static fromDto(dto: ReactionSocialPostDto): ReactionSocialPostEntity {
    return new ReactionSocialPostEntity({
      myReaction: dto.myReaction,
      allGroupedReactions: dto.allGroupedReactions,
    })
  }

  get totalReactions(): number {
    return this.allGroupedReactions.reduce((sum, r) => sum + r.number, 0)
  }
}

/* ── Full Reaction (user detail) ── */

export class ReactionEntity {
  emoji: string
  user: UserEntity | null
  creationDate: string

  private constructor(data: ReactionEntity) {
    this.emoji = data.emoji
    this.user = data.user
    this.creationDate = data.creationDate
  }

  static fromDto(dto: ReactionDto): ReactionEntity {
    return new ReactionEntity({
      emoji: dto.emoji,
      user: dto.user ? UserEntity.fromDto(dto.user) : null,
      creationDate: dto.creationDate,
    })
  }
}

/* ── Comment Mention ── */

export type CommentMentionEntity = CommentMentionDto

/* ── Comment ── */

export class CommentEntity {
  id: string
  text: string
  parentCommentId: string | null
  childrenComments: CommentEntity[]
  user: UserEntity | null
  creationDate: string
  reactions: ReactionSocialPostEntity
  mentions: CommentMentionEntity[]
  childrenCount: number

  private constructor(data: Omit<CommentEntity, "getCreationDateLabel">) {
    this.id = data.id
    this.text = data.text
    this.parentCommentId = data.parentCommentId
    this.childrenComments = data.childrenComments
    this.user = data.user
    this.creationDate = data.creationDate
    this.reactions = data.reactions
    this.mentions = data.mentions
    this.childrenCount = data.childrenCount
  }

  static fromDto(dto: CommentDto): CommentEntity {
    return new CommentEntity({
      id: dto.id,
      text: dto.text,
      parentCommentId: dto.parentCommentId,
      childrenComments: dto.childrenComments.map((c) => CommentEntity.fromDto(c)),
      user: dto.userDto ? UserEntity.fromDto(dto.userDto) : null,
      creationDate: dto.creationDate,
      reactions: ReactionSocialPostEntity.fromDto(dto.reactions),
      mentions: dto.mentions,
      childrenCount: dto.childrenCount,
    })
  }

  getCreationDateLabel(): string {
    const now = dayjs()
    const commentDate = dayjs(this.creationDate)
    const diffInMinutes = now.diff(commentDate, "minute")
    const diffInHours = now.diff(commentDate, "hour")
    const diffInDays = now.diff(commentDate, "day")
    const diffInWeeks = now.diff(commentDate, "week")
    const diffInMonths = now.diff(commentDate, "month")
    const diffInYears = now.diff(commentDate, "year")

    if (diffInMinutes < 1) return word("global.now.label")
    if (diffInHours < 1) return `${diffInMinutes} ${word("global.minute.label", { count: diffInMinutes })}`
    if (diffInDays < 1) return `${diffInHours} ${word("global.hour.label", { count: diffInHours })}`
    if (diffInWeeks < 1) return `${diffInDays} ${word("global.day.label", { count: diffInDays })}`
    if (diffInMonths < 1) return `${diffInWeeks} ${word("global.week.label", { count: diffInWeeks })}`
    if (diffInYears < 1) return `${diffInMonths} ${word("global.month.label", { count: diffInMonths })}`
    return `${diffInYears} ${word("global.year.label", { count: diffInYears })}`
  }
}

/* ── Social Post ── */

export class SocialPostEntity {
  id: string
  text: string | null
  creationDate: string
  reactions: ReactionSocialPostEntity
  title: string | null
  commentsNumber: number

  private constructor(data: SocialPostEntity) {
    this.id = data.id
    this.text = data.text
    this.creationDate = data.creationDate
    this.reactions = data.reactions
    this.title = data.title
    this.commentsNumber = data.commentsNumber
  }

  static fromDto(dto: SocialPostDto): SocialPostEntity {
    return new SocialPostEntity({
      id: dto.id,
      text: dto.text,
      creationDate: dto.creationDate,
      reactions: ReactionSocialPostEntity.fromDto(dto.reactions),
      title: dto.title,
      commentsNumber: dto.commentsNumber,
    })
  }
}

/* ── Social Post Resume Light (from session DTO) ── */

export class SocialPostResumeLightEntity {
  id: string | null
  reactions: ReactionSocialPostEntity
  commentsNumber: number

  private constructor(data: SocialPostResumeLightEntity) {
    this.id = data.id
    this.reactions = data.reactions
    this.commentsNumber = data.commentsNumber
  }

  static fromDto(dto: SocialPostResumeLightDto): SocialPostResumeLightEntity {
    return new SocialPostResumeLightEntity({
      id: dto.id,
      reactions: ReactionSocialPostEntity.fromDto(dto.reactions),
      commentsNumber: dto.commentsNumber,
    })
  }
}
